import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { okrErrorDescription } from "@/lib/okr-errors";
import { useCreateKeyResult, useUpdateKeyResult } from "@/hooks/useKeyResults";
import { KeyResult, KeyResultStatus } from "@/types";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { UserCombobox } from "@/components/UserCombobox";
import { Loader2 } from "lucide-react";
import { OBJECTIVE_STATUSES, STATUS_CONFIG } from "@/components/okr/okrConstants";
import { dueAfterStart, okrDateFields } from "./okr-date-schema";
import { CONTEXT_TEMPLATE, ContextMarkdownField, contextPatchForSubmit } from "./context-markdown-field";

const keyResultSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters").max(500),
  description: z.string().max(2000).optional().or(z.literal("")),
  context: z.string().max(10000, "Context must be at most 10000 characters").optional().or(z.literal("")),
  ...okrDateFields,
  objectivePid: z.string().optional().or(z.literal("")),
  ownerUserPid: z.string().optional().or(z.literal("")),
  weight: z.coerce.number().min(1, "Weight must be at least 1%").max(100),
  targetProgress: z.coerce.number().min(0).max(100).optional(),
  status: z.nativeEnum(KeyResultStatus).optional(),
  progress: z.coerce.number().min(0).max(100).optional(),
}).superRefine(dueAfterStart);

type KeyResultFormValues = z.infer<typeof keyResultSchema>;

interface KeyResultFormProps {
  initialData?: Partial<KeyResult>;
  parentObjectivePid?: string;
  onSuccess?: (keyResult: KeyResult) => void;
  hideButtons?: boolean;
  onSubmitRef?: (submitFn: () => void) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
}

export function KeyResultForm({
  initialData,
  parentObjectivePid,
  onSuccess,
  hideButtons = false,
  onSubmitRef,
  onSubmittingChange,
}: KeyResultFormProps) {
  const { toast } = useToast();
  const isEditing = !!initialData?.pid;
  const createMutation = useCreateKeyResult();
  const updateMutation = useUpdateKeyResult();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm<KeyResultFormValues>({
    resolver: zodResolver(keyResultSchema),
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      // Creating: seed the why/how template. Editing: show what is stored, template only if empty.
      context: initialData?.pid ? (initialData.context ?? "") : CONTEXT_TEMPLATE,
      startDate: initialData?.startDate ? initialData.startDate.split("T")[0] : "",
      dueDate: initialData?.dueDate ? initialData.dueDate.split("T")[0] : "",
      objectivePid: initialData?.objective?.pid || parentObjectivePid || "",
      ownerUserPid: initialData?.owner?.pid || "",
      weight: initialData?.weight ?? 100,
      targetProgress: initialData?.targetProgress ?? 100,
      status: initialData?.pid
        ? (initialData.status ?? KeyResultStatus.ACTIVE)
        : undefined,
      progress: initialData?.progress ?? 0,
    },
  });

  const onSubmit = useCallback(async (values: KeyResultFormValues) => {
    setIsSubmitting(true);
    onSubmittingChange?.(true);
    try {
      const payload = {
        name: values.name,
        description: values.description || undefined,
        // Blanking a stored strategy while editing is an explicit clear (null), like the owner field.
        context: contextPatchForSubmit(values.context, isEditing && !!initialData?.context),
        startDate: values.startDate,
        dueDate: values.dueDate,
        objectivePid: values.objectivePid || undefined,
        // Editing an owned KR down to no owner is an explicit clear; the BE reads `null` as that.
        ownerUserPid: values.ownerUserPid || (isEditing && initialData?.owner ? null : undefined),
        weight: values.weight,
        targetProgress: values.targetProgress,
      };

      let result: KeyResult;
      if (isEditing && initialData?.pid) {
        result = await updateMutation.mutateAsync({
          pid: initialData.pid,
          data: { ...payload, status: values.status, progress: values.progress },
        });
        toast({ title: "Key result updated", description: "Changes saved successfully." });
      } else {
        result = await createMutation.mutateAsync(payload);
        toast({ title: "Key result added", description: `"${values.name}" has been added.` });
      }
      onSuccess?.(result);
    } catch (error) {
      toast({ title: "Error", description: okrErrorDescription(error, "Something went wrong. Please try again."), variant: "destructive" });
    } finally {
      setIsSubmitting(false);
      onSubmittingChange?.(false);
    }
  }, [isEditing, initialData?.pid, initialData?.owner, initialData?.context, createMutation, updateMutation, toast, onSuccess, onSubmittingChange]);

  useEffect(() => {
    if (onSubmitRef) {
      onSubmitRef(() => form.handleSubmit(onSubmit)());
    }
  }, [onSubmitRef, form, onSubmit]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField control={form.control} name="name" render={({ field }) => (
          <FormItem>
            <FormLabel>Name *</FormLabel>
            <FormControl><Input placeholder="e.g. Close 50 enterprise deals" {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />

        <FormField control={form.control} name="description" render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl><Textarea placeholder="Optional description..." rows={3} {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />

        <FormField control={form.control} name="context" render={({ field }) => (
          <FormItem>
            <FormLabel>Strategy</FormLabel>
            <FormControl>
              <ContextMarkdownField value={field.value ?? ""} onChange={field.onChange} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />

        <FormField control={form.control} name="ownerUserPid" render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>Owner</FormLabel>
            <FormControl>
              <UserCombobox
                value={field.value}
                onChange={field.onChange}
                initialUser={initialData?.owner ?? null}
                placeholder="Select owner…"
                clearable
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )} />

        {isEditing && (
          <FormField control={form.control} name="status" render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                <SelectContent>
                  {OBJECTIVE_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_CONFIG[s].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )} />
        )}

        <div className="grid grid-cols-2 gap-4">
          <FormField control={form.control} name="startDate" render={({ field }) => (
            <FormItem>
              <FormLabel>Start Date *</FormLabel>
              <FormControl><Input type="date" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="dueDate" render={({ field }) => (
            <FormItem>
              <FormLabel>Due Date *</FormLabel>
              <FormControl><Input type="date" {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <FormField control={form.control} name="weight" render={({ field }) => (
            <FormItem>
              <FormLabel>Weight % *</FormLabel>
              <FormControl><Input type="number" min={1} max={100} {...field} /></FormControl>
              <FormDescription className="text-[11px]">Share of the objective; normalized across its KRs.</FormDescription>
              <FormMessage />
            </FormItem>
          )} />
          <FormField control={form.control} name="targetProgress" render={({ field }) => (
            <FormItem>
              <FormLabel>Target</FormLabel>
              <FormControl><Input type="number" min={0} max={100} {...field} /></FormControl>
              <FormMessage />
            </FormItem>
          )} />
          {isEditing && (
            <FormField control={form.control} name="progress" render={({ field }) => (
              <FormItem>
                <FormLabel>Progress</FormLabel>
                <FormControl><Input type="number" min={0} max={100} {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
          )}
        </div>

        {!hideButtons && (
          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? "Update" : "Create"} Key Result
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}
